import { useContext, useEffect } from "react";
import { SpinnerCircular } from "@weirgroup/weir-dls";
import { useTranslation } from "react-i18next";
import { AppConfigContext } from "../../contexts/app-config.context";
import { getBusinessEntitiesDetail } from '../../api/users.api';
import { ACCESS_TOKEN, BUSINESS_ENTITY, GRAPH_TOKEN, ROLES, StorageService, USER_DATA } from '../../services/storage_service';
import "./styles.scss";
import { login } from "../../api/login.api";
import { Roles } from "../../constants/roles.constants";
import { acquireAccessToken, acquireGraphToken, getActiveAccount } from "../../auth/msal";
import { isTokenExpired } from "../../helpers/getTokens.helper";

interface Props {
  setShowMapPage: (arg: boolean) => void;
}

interface UserRoleType {
  role: string;
  roleId: string;
}

function AuthenticationPage(props: Readonly<Props>) {
  const { setShowMapPage } = props;
  const { t } = useTranslation();
  const appConfigStore = useContext(AppConfigContext);

  const getBusinessEntities = () => {
    const storedRole = StorageService.getItem(ROLES);
    let roles;
    if (storedRole) {
      roles = JSON.parse(storedRole);
    }
    if (roles) {
      return getBusinessEntitiesDetail(roles.roleId);
    }
  }

  const getToken = async () => {
    const activeAccount = getActiveAccount();

    if (!activeAccount) {
      const tokenResponse = await acquireAccessToken();
      const graphApiResponse = await acquireGraphToken();

      processToken(tokenResponse, graphApiResponse);
    } else {
      const tokenResponse = await acquireAccessToken(activeAccount.username); // This is the one i need to pass to IAMNav
      const graphApiResponse = await acquireGraphToken(activeAccount.username);
      processToken(tokenResponse, graphApiResponse);
    }
    setShowMapPage(true)
  };

  const processToken = async (accessToken: any, graphToken: any) => {
    StorageService.setItem(ACCESS_TOKEN, accessToken.accessToken);
    StorageService.setItem(GRAPH_TOKEN, graphToken.accessToken);

    appConfigStore.setUserData(accessToken.account);

    // process roles
    if (accessToken.idTokenClaims) {
      const { roles } = accessToken.idTokenClaims;
      const roleData: UserRoleType | null = getRoleByUser(roles);
      StorageService.setItem(ROLES, JSON.stringify(roleData));
    }

    const userData = getUserData(accessToken.account);
    StorageService.setItem(USER_DATA, JSON.stringify(userData));
    const businessEntities = await getBusinessEntities();

    if (businessEntities) {
      const businessEntityId = businessEntities[0].businessEntityObjectId;
      StorageService.setItem(BUSINESS_ENTITY, businessEntityId);
      await login(businessEntityId);

      setShowMapPage(true);
    }
  }

  const getUserData = (user: any) => {
    return {
      name: user.name ?? user.username,
      email: user.username,
    }
  }

  const getRoleByUser = (
    roles: string[]
  ): { role: string; roleId: string } | null => {
    let assignedRole: UserRoleType | null = null;
    const rolesDummy = [
      {
        role: Roles.APP_ADMIN,
        roleId: process.env.REACT_APP_APPLICATION_ADMINISTRATOR_ROLE_ID ?? '',
      },
      {
        role: Roles.ADMIN,
        roleId: process.env.REACT_APP_BUSINESS_ENTITY_ADMINISTRATOR_ROLE_ID ?? '',
      },
      {
        role: Roles.SUB_USER,
        roleId: process.env.REACT_APP_APPLICATION_USER_ROLE_ID ?? '',
      },
      {
        role: Roles.GUEST,
        roleId: process.env.REACT_APP_GUEST_ROLE_ID ?? '',
      },
    ];
    if (roles.length === 0) {
      assignedRole = null;
    } else {
      const r = roles[roles.length - 1];
      rolesDummy.forEach((role) => {
        if (r == role.role) {
          assignedRole = role;
        }
      })
    }
    return assignedRole;
  };

  useEffect(() => {
    if (isTokenExpired()) getToken();
    else setShowMapPage(true);
  }, []);

  return (
    <div className="authentication-page">
      <div className="login-spinner">
        <p>{t("authenticating")}</p>
        <SpinnerCircular />
      </div>
    </div>
  );
}

export default AuthenticationPage;